import i18n from '@/locales/i18n';
import { cloneDeep } from 'lodash-es';
import { buildVueFlowEdgeId } from '@/libs/flow/flowClipboardEdge';
const findNode = (nodes, nodeId) => nodes.find(node => node.id === nodeId);
const invalidGroupData = () => ({
  ok: false,
  error: 'invalid_group_data'
});
const regenerateEdgeId = edge => ({
  ...edge,
  id: buildVueFlowEdgeId({
    source: edge.source,
    sourceHandle: edge.sourceHandle,
    target: edge.target,
    targetHandle: edge.targetHandle
  })
});
const getIndependentBlockRecords = ({ nodes, blockId }) => {
  const block = findNode(nodes, blockId);
  if (
    !block ||
    block.type !== 'block' ||
    block.hidden === true ||
    isGroupBlock(block)
  ) {
    return invalidGroupData();
  }
  const memberIds = getGroupMemberIds(block);
  if (memberIds.length !== 1) {
    return invalidGroupData();
  }
  const child = findNode(nodes, memberIds[0]);
  if (!child || child.blockId !== block.id) {
    return invalidGroupData();
  }
  return {
    ok: true,
    block,
    child
  };
};
const normalizeMemberOrder = ({ memberIds, nodes }) => {
  const members = memberIds.map(memberId => findNode(nodes, memberId));
  if (members.some(member => !member)) {
    return invalidGroupData();
  }
  const requiredLastIds = members
    .filter(isNodeRequiredLast)
    .map(member => member.id);
  if (requiredLastIds.length > 1) {
    return {
      ok: false,
      error: 'multiple_terminal_members'
    };
  }
  if (!requiredLastIds.length) {
    return {
      ok: true,
      memberIds
    };
  }
  const requiredLastId = requiredLastIds[0];
  return {
    ok: true,
    memberIds: [
      ...memberIds.filter(memberId => memberId !== requiredLastId),
      requiredLastId
    ]
  };
};
const insertRelativeToTarget = ({
  memberIds,
  childId,
  targetChildId,
  placement
}) => {
  const nextMemberIds = memberIds.filter(memberId => memberId !== childId);
  const targetIndex = nextMemberIds.indexOf(targetChildId);
  if (targetIndex < 0 || !['before', 'after'].includes(placement)) {
    return null;
  }
  nextMemberIds.splice(
    placement === 'before' ? targetIndex : targetIndex + 1,
    0,
    childId
  );
  return nextMemberIds;
};
const replaceNode = (nodes, nodeId, updater) =>
  nodes.map(node => (node.id === nodeId ? updater(node) : node));
const updateGroupedChild = ({ nodes, childId, groupId, originalBlockId }) =>
  replaceNode(nodes, childId, child => ({
    ...child,
    blockId: groupId,
    originalBlockId
  }));
const setBlockHidden = ({ nodes, blockId, hidden }) =>
  replaceNode(nodes, blockId, block => ({
    ...block,
    hidden,
    ...(hidden
      ? {
          selected: false
        }
      : {})
  }));
const restoreBlockAtPosition = ({ block, position }) => ({
  ...block,
  hidden: false,
  position: cloneDeep(position),
  computedPosition: {
    ...block.computedPosition,
    x: position.x,
    y: position.y
  }
});
const getBoundarySourceHandle = ({ edge, lastChild }) => {
  const sourceHandles = getNodeSourceHandleIds(lastChild);
  return (
    sourceHandles.find(
      sourceHandle => String(sourceHandle) === String(edge.sourceHandle)
    ) || sourceHandles[0]
  );
};
const getNodeExceptionBranchHandleId = node =>
  node?.config?.exception_handling?.exception_branch_handle_id;
const getNodeExternalSourceHandleIds = node =>
  [
    ...getNodeSourceHandleIds(node),
    getNodeExceptionBranchHandleId(node)
  ].filter(Boolean);
const getGroupExceptionSourceHandleIds = ({ group, nodes }) =>
  new Set(
    getGroupMemberIds(group)
      .map(memberId =>
        getNodeExceptionBranchHandleId(findNode(nodes, memberId))
      )
      .filter(Boolean)
      .map(String)
  );
const isInternalEdgeForGroup = ({ edge, groupId }) =>
  edge.data?.is_group_internal === true &&
  (edge.source === groupId || edge.target === groupId);
const rebuildGroupEdges = ({ edges, nodes, group, replacedBlockIds = [] }) => {
  const replacedBlockIdSet = new Set(replacedBlockIds);
  const memberIds = getGroupMemberIds(group);
  const lastChild = findNode(nodes, memberIds.at(-1));
  const exceptionSourceHandleIds = getGroupExceptionSourceHandleIds({
    group,
    nodes
  });
  const rebuiltExternalEdges = edges
    .filter(
      edge =>
        !isInternalEdgeForGroup({
          edge,
          groupId: group.id
        })
    )
    .filter(edge => {
      const sourceWillUseGroup =
        replacedBlockIdSet.has(edge.source) || edge.source === group.id;
      const targetWillUseGroup =
        replacedBlockIdSet.has(edge.target) || edge.target === group.id;
      return !(sourceWillUseGroup && targetWillUseGroup);
    })
    .map(edge => {
      let nextEdge = edge;
      let changed = false;
      if (replacedBlockIdSet.has(nextEdge.target)) {
        nextEdge = {
          ...nextEdge,
          target: group.id
        };
        changed = true;
      }
      if (
        replacedBlockIdSet.has(nextEdge.source) ||
        nextEdge.source === group.id
      ) {
        const sourceHandle = exceptionSourceHandleIds.has(
          String(nextEdge.sourceHandle)
        )
          ? nextEdge.sourceHandle
          : getBoundarySourceHandle({
              edge: nextEdge,
              lastChild
            });
        if (typeof sourceHandle === 'undefined') {
          return null;
        }
        nextEdge = {
          ...nextEdge,
          source: group.id,
          sourceHandle
        };
        changed = true;
      }
      return changed ? regenerateEdgeId(nextEdge) : nextEdge;
    })
    .filter(Boolean);
  return [
    ...rebuiltExternalEdges,
    ...buildGroupInternalEdges({
      group,
      nodes
    })
  ];
};
const getGroupRecords = ({ nodes, groupId }) => {
  const group = findNode(nodes, groupId);
  if (!isGroupBlock(group)) {
    return invalidGroupData();
  }
  const memberIds = getGroupMemberIds(group);
  if (!memberIds.length) {
    return invalidGroupData();
  }
  const children = memberIds.map(memberId => findNode(nodes, memberId));
  if (
    children.some(
      child => !child || child.blockId !== group.id || !child.originalBlockId
    )
  ) {
    return invalidGroupData();
  }
  const originalBlocks = children.map(child =>
    findNode(nodes, child.originalBlockId)
  );
  const hasInvalidOriginalBlock = originalBlocks.some(
    (block, index) =>
      !block ||
      block.type !== 'block' ||
      block.hidden !== true ||
      getGroupMemberIds(block).length !== 1 ||
      getGroupMemberIds(block)[0] !== children[index].id
  );
  if (hasInvalidOriginalBlock) {
    return invalidGroupData();
  }
  return {
    ok: true,
    group,
    memberIds,
    children,
    originalBlocks
  };
};
const restoreChildNode = ({ child, originalBlockId }) => {
  const restoredChild = {
    ...child,
    blockId: originalBlockId
  };
  delete restoredChild.originalBlockId;
  return restoredChild;
};
const dissolveSingleMemberGroup = ({ nodes, edges, groupId }) => {
  const records = getGroupRecords({
    nodes,
    groupId
  });
  if (!records.ok || records.memberIds.length !== 1) {
    return invalidGroupData();
  }
  const child = records.children[0];
  const originalBlock = records.originalBlocks[0];
  const nextNodes = nodes
    .filter(node => node.id !== groupId)
    .map(node => {
      if (node.id === originalBlock.id) {
        return restoreBlockAtPosition({
          block: node,
          position: records.group.position
        });
      }
      if (node.id === child.id) {
        return restoreChildNode({
          child: node,
          originalBlockId: originalBlock.id
        });
      }
      return node;
    });
  const nextEdges = edges
    .filter(
      edge =>
        !isInternalEdgeForGroup({
          edge,
          groupId
        })
    )
    .map(edge => {
      let nextEdge = edge;
      let changed = false;
      if (nextEdge.target === groupId) {
        nextEdge = {
          ...nextEdge,
          target: originalBlock.id,
          targetHandle: child.id
        };
        changed = true;
      }
      if (nextEdge.source === groupId) {
        const sourceHandle = getBoundarySourceHandle({
          edge: nextEdge,
          lastChild: child
        });
        if (typeof sourceHandle === 'undefined') {
          return null;
        }
        nextEdge = {
          ...nextEdge,
          source: originalBlock.id,
          sourceHandle
        };
        changed = true;
      }
      return changed ? regenerateEdgeId(nextEdge) : nextEdge;
    })
    .filter(Boolean);
  return {
    ok: true,
    nodes: nextNodes,
    edges: nextEdges
  };
};
export const getNodeSourceHandleIds = node => {
  if (node.type === 'condition') {
    return (node.config?.if_else_conditions || [])
      .map(item => item.condition_id)
      .filter(Boolean)
      .map(String);
  }
  if (node.type === 'semanticJudgment') {
    return [
      ...(node.config?.semantic_conditions || []),
      node.config?.default_condition
    ]
      .filter(Boolean)
      .map(item => item.condition_id)
      .filter(value => value !== undefined && value !== null && value !== '')
      .map(String);
  }
  if (node.type === 'llMReply') {
    const llmConfig = node.config?.llm_config;
    return [
      llmConfig?.main_condition_id,
      llmConfig?.verify_enable ? llmConfig?.other_condition_id : undefined
    ].filter(Boolean);
  }
  if (
    node.type === 'captureSlotEntity' &&
    node.config?.collect_mode === 'function_call' &&
    node.config?.function_call?.verify_type === '1'
  ) {
    const sourceHandle = node.data?.sourceHandle;
    const failHandle = node.data.failHandle;
    return [sourceHandle, failHandle].filter(Boolean);
  }
  return node.data?.sourceHandle ? [node.data.sourceHandle] : [];
};
export const isGroupBlock = node =>
  node?.type === 'block' && node.data?.is_group === true;
export const hasActiveExceptionBranch = node =>
  node?.config?.exception_handling?.enabled === true &&
  node?.config?.exception_handling?.strategy === 'exception_branch';
export const isNodeRequiredLast = node =>
  getNodeSourceHandleIds(node).length !== 1 || hasActiveExceptionBranch(node);
export const getGroupMemberIds = block =>
  Array.isArray(block?.data?.include_node_ids)
    ? block.data.include_node_ids
    : [];
export const getDropPlacement = ({ clientY, top, height }) =>
  clientY < top + height / 2 ? 'before' : 'after';
export const getDetachedBlockClientPosition = ({
  clientOffset,
  dragPointerOffset
}) => {
  if (!clientOffset || !dragPointerOffset) {
    return null;
  }
  return {
    x: clientOffset.x - dragPointerOffset.x,
    y: clientOffset.y - dragPointerOffset.y
  };
};
export const validateGroupOrder = ({ memberIds, nodes }) => {
  const members = memberIds.map(memberId =>
    nodes.find(node => node.id === memberId)
  );
  if (members.some(member => !member)) {
    return {
      ok: false,
      error: 'invalid_group_data'
    };
  }
  const requiredLastMembers = members.filter(isNodeRequiredLast);
  if (requiredLastMembers.length > 1) {
    return {
      ok: false,
      error: 'multiple_terminal_members'
    };
  }
  if (
    requiredLastMembers.length === 1 &&
    requiredLastMembers[0].id !== memberIds.at(-1)
  ) {
    return {
      ok: false,
      error: 'terminal_member_not_last'
    };
  }
  return {
    ok: true
  };
};
export const buildGroupInternalEdge = ({
  groupId,
  sourceChild,
  targetChild
}) => {
  const sourceHandle = getNodeSourceHandleIds(sourceChild)[0];
  const targetHandle = targetChild.id;
  return {
    id: buildVueFlowEdgeId({
      source: groupId,
      sourceHandle,
      target: groupId,
      targetHandle
    }),
    source: groupId,
    sourceHandle,
    target: groupId,
    targetHandle,
    type: 'custom',
    hidden: true,
    data: {
      is_group_internal: true
    }
  };
};
export const buildGroupInternalEdges = ({ group, nodes }) => {
  const memberIds = getGroupMemberIds(group);
  return memberIds.slice(0, -1).map((sourceId, index) => {
    const sourceChild = nodes.find(node => node.id === sourceId);
    const targetChild = nodes.find(node => node.id === memberIds[index + 1]);
    return buildGroupInternalEdge({
      groupId: group.id,
      sourceChild,
      targetChild
    });
  });
};
export const mergeIndependentBlocks = ({
  nodes,
  edges,
  sourceBlockId,
  targetBlockId,
  placement,
  groupId
}) => {
  const nextNodesInput = cloneDeep(nodes);
  const nextEdgesInput = cloneDeep(edges);
  const sourceBlock = findNode(nextNodesInput, sourceBlockId);
  if (isGroupBlock(sourceBlock)) {
    return {
      ok: false,
      error: 'group_source_not_allowed'
    };
  }
  if (
    sourceBlockId === targetBlockId ||
    findNode(nextNodesInput, groupId) ||
    !['before', 'after'].includes(placement)
  ) {
    return invalidGroupData();
  }
  const sourceRecords = getIndependentBlockRecords({
    nodes: nextNodesInput,
    blockId: sourceBlockId
  });
  const targetRecords = getIndependentBlockRecords({
    nodes: nextNodesInput,
    blockId: targetBlockId
  });
  if (!sourceRecords.ok || !targetRecords.ok) {
    return invalidGroupData();
  }
  const intendedMemberIds =
    placement === 'before'
      ? [sourceRecords.child.id, targetRecords.child.id]
      : [targetRecords.child.id, sourceRecords.child.id];
  const normalizedOrder = normalizeMemberOrder({
    memberIds: intendedMemberIds,
    nodes: nextNodesInput
  });
  if (!normalizedOrder.ok) {
    return normalizedOrder;
  }
  const group = {
    ...targetRecords.block,
    id: groupId,
    hidden: false,
    selected: true,
    data: {
      ...targetRecords.block.data,
      label: i18n.global.t(
        'views.flow.FlowGrap.nodeGrouping.3c1a52844e2adbb3d4299c2070886517'
      ),
      is_group: true,
      include_node_ids: normalizedOrder.memberIds
    }
  };
  let nextNodes = [...nextNodesInput, group];
  nextNodes = setBlockHidden({
    nodes: nextNodes,
    blockId: sourceBlockId,
    hidden: true
  });
  nextNodes = setBlockHidden({
    nodes: nextNodes,
    blockId: targetBlockId,
    hidden: true
  });
  nextNodes = updateGroupedChild({
    nodes: nextNodes,
    childId: sourceRecords.child.id,
    groupId,
    originalBlockId: sourceBlockId
  });
  nextNodes = updateGroupedChild({
    nodes: nextNodes,
    childId: targetRecords.child.id,
    groupId,
    originalBlockId: targetBlockId
  });
  return {
    ok: true,
    nodes: nextNodes,
    edges: rebuildGroupEdges({
      edges: nextEdgesInput,
      nodes: nextNodes,
      group,
      replacedBlockIds: [sourceBlockId, targetBlockId]
    })
  };
};
export const insertChildIntoGroup = ({
  nodes,
  edges,
  sourceBlockId,
  targetGroupId,
  targetChildId,
  placement
}) => {
  const nextNodesInput = cloneDeep(nodes);
  const nextEdgesInput = cloneDeep(edges);
  const sourceBlock = findNode(nextNodesInput, sourceBlockId);
  if (isGroupBlock(sourceBlock)) {
    return {
      ok: false,
      error: 'group_source_not_allowed'
    };
  }
  const sourceRecords = getIndependentBlockRecords({
    nodes: nextNodesInput,
    blockId: sourceBlockId
  });
  const targetGroup = findNode(nextNodesInput, targetGroupId);
  if (!sourceRecords.ok || !isGroupBlock(targetGroup)) {
    return invalidGroupData();
  }
  const currentMemberIds = getGroupMemberIds(targetGroup);
  const intendedMemberIds = insertRelativeToTarget({
    memberIds: currentMemberIds,
    childId: sourceRecords.child.id,
    targetChildId,
    placement
  });
  if (!intendedMemberIds) {
    return invalidGroupData();
  }
  const normalizedOrder = normalizeMemberOrder({
    memberIds: intendedMemberIds,
    nodes: nextNodesInput
  });
  if (!normalizedOrder.ok) {
    return normalizedOrder;
  }
  const group = {
    ...targetGroup,
    selected: true,
    data: {
      ...targetGroup.data,
      include_node_ids: normalizedOrder.memberIds
    }
  };
  let nextNodes = replaceNode(nextNodesInput, targetGroupId, () => group);
  nextNodes = setBlockHidden({
    nodes: nextNodes,
    blockId: sourceBlockId,
    hidden: true
  });
  nextNodes = updateGroupedChild({
    nodes: nextNodes,
    childId: sourceRecords.child.id,
    groupId: targetGroupId,
    originalBlockId: sourceBlockId
  });
  return {
    ok: true,
    nodes: nextNodes,
    edges: rebuildGroupEdges({
      edges: nextEdgesInput,
      nodes: nextNodes,
      group,
      replacedBlockIds: [sourceBlockId]
    })
  };
};
export const reorderGroupChild = ({
  nodes,
  edges,
  groupId,
  childId,
  targetChildId,
  placement
}) => {
  const nextNodesInput = cloneDeep(nodes);
  const nextEdgesInput = cloneDeep(edges);
  const targetGroup = findNode(nextNodesInput, groupId);
  if (!isGroupBlock(targetGroup)) {
    return invalidGroupData();
  }
  const currentMemberIds = getGroupMemberIds(targetGroup);
  if (
    !currentMemberIds.includes(childId) ||
    !currentMemberIds.includes(targetChildId)
  ) {
    return invalidGroupData();
  }
  if (childId === targetChildId) {
    return {
      ok: true,
      nodes: nextNodesInput,
      edges: nextEdgesInput
    };
  }
  const intendedMemberIds = insertRelativeToTarget({
    memberIds: currentMemberIds,
    childId,
    targetChildId,
    placement
  });
  if (!intendedMemberIds) {
    return invalidGroupData();
  }
  const normalizedOrder = normalizeMemberOrder({
    memberIds: intendedMemberIds,
    nodes: nextNodesInput
  });
  if (!normalizedOrder.ok) {
    return normalizedOrder;
  }
  const group = {
    ...targetGroup,
    data: {
      ...targetGroup.data,
      include_node_ids: normalizedOrder.memberIds
    }
  };
  const nextNodes = replaceNode(nextNodesInput, groupId, () => group);
  return {
    ok: true,
    nodes: nextNodes,
    edges: rebuildGroupEdges({
      edges: nextEdgesInput,
      nodes: nextNodes,
      group
    })
  };
};
const activateGroupedNodeRequiredLast = ({
  nodes,
  edges,
  nodeId,
  updateNode
}) => {
  const nextNodesInput = cloneDeep(nodes);
  const nextEdgesInput = cloneDeep(edges);
  let nextNodes = updateNode
    ? replaceNode(nextNodesInput, nodeId, updateNode)
    : nextNodesInput;
  const child = findNode(nextNodes, nodeId);
  if (!child) {
    return invalidGroupData();
  }
  const group = findNode(nextNodes, child.blockId);
  if (!isGroupBlock(group)) {
    return {
      ok: true,
      nodes: nextNodes,
      edges: nextEdgesInput
    };
  }
  const memberIds = getGroupMemberIds(group);
  if (!memberIds.includes(nodeId)) {
    return invalidGroupData();
  }
  const hasAnotherRequiredLastNode = memberIds
    .filter(memberId => memberId !== nodeId)
    .map(memberId => findNode(nextNodes, memberId))
    .some(node => !node || isNodeRequiredLast(node));
  if (hasAnotherRequiredLastNode) {
    return {
      ok: false,
      error: 'group_branch_only_last'
    };
  }
  const orderedMemberIds = [
    ...memberIds.filter(memberId => memberId !== nodeId),
    nodeId
  ];
  const nextGroup = {
    ...group,
    data: {
      ...group.data,
      include_node_ids: orderedMemberIds
    }
  };
  nextNodes = replaceNode(nextNodes, group.id, () => nextGroup);
  return {
    ok: true,
    nodes: nextNodes,
    edges: rebuildGroupEdges({
      edges: nextEdgesInput,
      nodes: nextNodes,
      group: nextGroup
    })
  };
};
export const activateGroupedNodeExceptionBranch = ({ nodes, edges, nodeId }) =>
  activateGroupedNodeRequiredLast({
    nodes,
    edges,
    nodeId,
    updateNode: node => ({
      ...node,
      config: {
        ...node.config,
        exception_handling: {
          ...node.config?.exception_handling,
          strategy: 'exception_branch'
        }
      }
    })
  });
export const activateGroupedNodeSemanticBranch = ({ nodes, edges, nodeId }) =>
  activateGroupedNodeRequiredLast({
    nodes,
    edges,
    nodeId
  });
export const detachChildFromGroup = ({
  nodes,
  edges,
  groupId,
  childId,
  position
}) => {
  const nextNodesInput = cloneDeep(nodes);
  const nextEdgesInput = cloneDeep(edges);
  const records = getGroupRecords({
    nodes: nextNodesInput,
    groupId
  });
  if (!records.ok || records.memberIds.length < 2) {
    return invalidGroupData();
  }
  const childIndex = records.memberIds.indexOf(childId);
  if (childIndex < 0) {
    return invalidGroupData();
  }
  const detachedChild = records.children[childIndex];
  const detachedOriginalBlock = records.originalBlocks[childIndex];
  const remainingMemberIds = records.memberIds.filter(
    memberId => memberId !== childId
  );
  const remainingOrderValidation = validateGroupOrder({
    memberIds: remainingMemberIds,
    nodes: nextNodesInput
  });
  if (!remainingOrderValidation.ok) {
    return remainingOrderValidation;
  }
  const detachedSourceHandles = new Set(
    getNodeExternalSourceHandleIds(detachedChild).map(String)
  );
  const detachedExceptionHandleId =
    getNodeExceptionBranchHandleId(detachedChild);
  const externalIncomingEdges = nextEdgesInput.filter(
    edge =>
      edge.data?.is_group_internal !== true &&
      edge.target === groupId &&
      edge.targetHandle === detachedChild.id
  );
  const externalOutgoingEdges = nextEdgesInput.filter(
    edge =>
      edge.data?.is_group_internal !== true &&
      edge.source === groupId &&
      detachedSourceHandles.has(String(edge.sourceHandle))
  );
  const edgesWithoutDetachedChild = nextEdgesInput.filter(
    edge =>
      !isInternalEdgeForGroup({
        edge,
        groupId
      }) &&
      edge.targetHandle !== detachedChild.id &&
      !detachedSourceHandles.has(String(edge.sourceHandle))
  );
  const remainingGroup = {
    ...records.group,
    data: {
      ...records.group.data,
      include_node_ids: remainingMemberIds
    }
  };
  let nextNodes = replaceNode(nextNodesInput, detachedOriginalBlock.id, block =>
    restoreBlockAtPosition({
      block,
      position
    })
  );
  nextNodes = replaceNode(nextNodes, detachedChild.id, child =>
    restoreChildNode({
      child,
      originalBlockId: detachedOriginalBlock.id
    })
  );
  nextNodes = replaceNode(nextNodes, groupId, () => remainingGroup);
  const firstRemainingChildId = remainingMemberIds[0];
  const lastRemainingChild = findNode(nextNodes, remainingMemberIds.at(-1));
  const migratedIncomingEdges = externalIncomingEdges.map(edge =>
    regenerateEdgeId({
      ...edge,
      target: groupId,
      targetHandle: firstRemainingChildId
    })
  );
  const migratedOutgoingEdges = externalOutgoingEdges.map(edge => {
    if (
      detachedExceptionHandleId &&
      String(edge.sourceHandle) === String(detachedExceptionHandleId)
    ) {
      return regenerateEdgeId({
        ...edge,
        source: detachedOriginalBlock.id,
        sourceHandle: detachedExceptionHandleId
      });
    }
    return regenerateEdgeId({
      ...edge,
      source: groupId,
      sourceHandle: getBoundarySourceHandle({
        edge,
        lastChild: lastRemainingChild
      })
    });
  });
  const nextEdges = rebuildGroupEdges({
    edges: [
      ...edgesWithoutDetachedChild,
      ...migratedIncomingEdges,
      ...migratedOutgoingEdges
    ],
    nodes: nextNodes,
    group: remainingGroup
  });
  if (remainingMemberIds.length === 1) {
    return dissolveSingleMemberGroup({
      nodes: nextNodes,
      edges: nextEdges,
      groupId
    });
  }
  return {
    ok: true,
    nodes: nextNodes,
    edges: nextEdges
  };
};
export const deleteChildFromGroup = ({ nodes, edges, groupId, childId }) => {
  const child = findNode(nodes, childId);
  const originalBlockId = child?.originalBlockId;
  if (!originalBlockId) {
    return invalidGroupData();
  }
  const detached = detachChildFromGroup({
    nodes,
    edges,
    groupId,
    childId,
    position: findNode(nodes, groupId)?.position
  });
  if (!detached.ok) {
    return detached;
  }
  const removedNodeIds = new Set([childId, originalBlockId]);
  const removedSourceHandles = new Set(
    getNodeExternalSourceHandleIds(child).map(String)
  );
  return {
    ok: true,
    nodes: detached.nodes.filter(node => !removedNodeIds.has(node.id)),
    edges: detached.edges.filter(
      edge =>
        !removedNodeIds.has(edge.source) &&
        !removedNodeIds.has(edge.target) &&
        edge.targetHandle !== childId &&
        !removedSourceHandles.has(String(edge.sourceHandle))
    )
  };
};
export const deleteNodeGroup = ({ nodes, edges, groupId }) => {
  const nextNodesInput = cloneDeep(nodes);
  const nextEdgesInput = cloneDeep(edges);
  const records = getGroupRecords({
    nodes: nextNodesInput,
    groupId
  });
  if (!records.ok) {
    return invalidGroupData();
  }
  const removedNodeIds = new Set([
    groupId,
    ...records.memberIds,
    ...records.originalBlocks.map(block => block.id)
  ]);
  const removedTargetHandles = new Set(records.memberIds);
  const removedSourceHandles = new Set(
    records.children.flatMap(getNodeSourceHandleIds).map(String)
  );
  return {
    ok: true,
    nodes: nextNodesInput.filter(node => !removedNodeIds.has(node.id)),
    edges: nextEdgesInput.filter(
      edge =>
        !removedNodeIds.has(edge.source) &&
        !removedNodeIds.has(edge.target) &&
        !removedTargetHandles.has(edge.targetHandle) &&
        !removedSourceHandles.has(String(edge.sourceHandle))
    )
  };
};
export const normalizeLoadedNodeGroups = ({ nodes, edges }) => {
  let nextNodes = cloneDeep(nodes);
  let nextEdges = cloneDeep(edges);
  const groupIds = nextNodes.filter(isGroupBlock).map(group => group.id);
  for (const groupId of groupIds) {
    const records = getGroupRecords({
      nodes: nextNodes,
      groupId
    });
    if (!records.ok) {
      return invalidGroupData();
    }
    const orderValidation = validateGroupOrder({
      memberIds: records.memberIds,
      nodes: nextNodes
    });
    if (!orderValidation.ok) {
      return orderValidation;
    }
    if (records.memberIds.length === 1) {
      const dissolved = dissolveSingleMemberGroup({
        nodes: nextNodes,
        edges: nextEdges,
        groupId
      });
      if (!dissolved.ok) {
        return invalidGroupData();
      }
      nextNodes = dissolved.nodes;
      nextEdges = dissolved.edges;
    }
  }
  return {
    ok: true,
    nodes: nextNodes,
    edges: nextEdges
  };
};
export const canStartGroupConnection = ({
  nodes,
  sourceNodeId,
  sourceHandle,
  edgeData
}) => {
  if (
    edgeData?.is_group_internal === true ||
    edgeData?.edgeType === 'trigger'
  ) {
    return true;
  }
  const sourceNode = findNode(nodes, sourceNodeId);
  if (!sourceNode) {
    return false;
  }
  if (!isGroupBlock(sourceNode)) {
    return true;
  }
  const startsFromMemberExceptionBranch = getGroupMemberIds(sourceNode)
    .map(memberId => findNode(nodes, memberId))
    .some(member => {
      const handling = member?.config?.exception_handling;
      return (
        handling?.enabled === true &&
        handling?.strategy === 'exception_branch' &&
        String(handling.exception_branch_handle_id) === String(sourceHandle)
      );
    });
  if (startsFromMemberExceptionBranch) {
    return true;
  }
  const lastChildId = getGroupMemberIds(sourceNode).at(-1);
  const lastChild = findNode(nodes, lastChildId);
  if (!lastChild) {
    return false;
  }
  return getNodeSourceHandleIds(lastChild).some(
    handleId => String(handleId) === String(sourceHandle)
  );
};
export const syncIndependentBlockTitles = nodes => {
  const nextNodes = cloneDeep(nodes);
  const blockNodeTitleMap = new Map();
  nextNodes.forEach(node => {
    if (node.type !== 'block' || node.data?.is_group === true) {
      return;
    }
    const blockLabel = node.data?.label;
    if (typeof blockLabel === 'undefined') {
      return;
    }
    getGroupMemberIds(node).forEach(childId => {
      blockNodeTitleMap.set(childId, blockLabel);
    });
  });
  return nextNodes.map(node => {
    const blockLabel = blockNodeTitleMap.get(node.id);
    const hasConfigTitle =
      node.config && Object.prototype.hasOwnProperty.call(node.config, 'title');
    if (typeof blockLabel === 'undefined' || !hasConfigTitle) {
      return node;
    }
    return {
      ...node,
      config: {
        ...node.config,
        title: blockLabel
      }
    };
  });
};
